import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AlertTriangle, Check, CornerDownLeft } from 'lucide-react';
import {
  AdminPage,
  Button,
  ErrorState,
  Panel,
  Progress,
  Skeleton,
  StatusBadge,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { MODULE_LABELS, type ModuleKey } from '@/access/modules';
import { cn } from '@/lib/cn';
import { errorMessage } from '@/lib/errors';
import { usePlatformBoard, usePlatformMutations, type AiQuota, type FeatureNode } from './api';
import { AI_MODULES, MODULE_EFFECT, MODULE_GROUPS } from './moduleGroups';

/**
 * לוח הבקרה של הפלטפורמה.
 *
 * שתי שאלות שונות באותו מסך, כי שתיהן "מה המערכת מרשה לעצמה":
 * מה הצוות רואה, וכמה כסף מותר ל AI לשרוף החודש.
 *
 * העץ מגיע מהשרת ולא מרשימה בקוד, ולכן פיצול מודול נוסף לתת
 * פיצ'רים הוא שורה בטבלה.
 */
export function PlatformPage() {
  const canManage = usePerm('platform.manage');
  const board = usePlatformBoard();
  const mutations = usePlatformMutations();
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  const run = (action: Promise<unknown>) => {
    setError(null);
    void action.catch((caught: unknown) => setError(errorMessage(caught)));
  };

  const features = board.data?.features ?? [];
  const budgets = board.data?.budgets ?? [];
  const byKey = new Map(features.map((feature) => [feature.key, feature]));
  const childrenOf = (key: ModuleKey) =>
    features.filter((feature) => feature.parent_key === key);

  return (
    <AdminPage title="פלטפורמה" subtitle="מה דולק באפליקציה, וכמה AI מותר החודש">
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {board.isPending && <Skeleton className="h-64" />}
      {board.isError && <ErrorState onRetry={() => void board.refetch()} />}

      {board.data && (
        <>
          {MODULE_GROUPS.map((group) => (
            <Panel key={group.title} title={group.title}>
              <p className="m-0 mb-3 text-body-sm text-muted">{group.hint}</p>

              <ul className="m-0 grid list-none gap-2 p-0">
                {group.keys.map((key) => {
                  const node = byKey.get(key);
                  if (!node) return null;
                  const kids = childrenOf(key);

                  return (
                    <li key={key} className="grid gap-2">
                      <FeatureRow
                        node={node}
                        isSaving={mutations.setModule.isPending}
                        onToggle={(enabled) =>
                          run(mutations.setModule.mutateAsync({ key, enabled }))
                        }
                      />

                      {kids.length > 0 && (
                        /* מוזח, כדי שיהיה ברור מה שייך למה */
                        <ul className="m-0 grid list-none gap-2 p-0 ps-6">
                          {kids.map((kid) => (
                            <li key={kid.key}>
                              <FeatureRow
                                node={kid}
                                parentOff={!node.effective}
                                isSaving={mutations.setModule.isPending}
                                onToggle={(enabled) =>
                                  run(
                                    mutations.setModule.mutateAsync({
                                      key: kid.key,
                                      enabled,
                                    }),
                                  )
                                }
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Panel>
          ))}

          <Panel title="תקציב AI">
            <p className="m-0 mb-3 text-body-sm text-muted">
              תקרה חודשית לכל מודול. כשהיא נגמרת הפעולה נחסמת בשרת ולא רק במסך, והמונה מתאפס
              בתחילת כל חודש.
            </p>

            <div className="grid gap-3">
              {AI_MODULES.map((key) => (
                <BudgetRow
                  key={key}
                  moduleKey={key}
                  quota={budgets.find((budget) => budget.module_key === key) ?? null}
                  moduleOn={byKey.get(key)?.effective === true}
                  isSaving={mutations.setBudget.isPending}
                  onSave={(limit, active) =>
                    run(mutations.setBudget.mutateAsync({ module: key, limit, active }))
                  }
                />
              ))}
            </div>
          </Panel>
        </>
      )}
    </AdminPage>
  );
}

function FeatureRow({
  node,
  parentOff = false,
  isSaving,
  onToggle,
}: {
  node: FeatureNode;
  /** ההורה כבוי: הילד כבוי בפועל, ואין טעם לתת לכבות אותו שוב. */
  parentOff?: boolean;
  isSaving: boolean;
  onToggle: (enabled: boolean) => void;
}) {
  const effect = MODULE_EFFECT[node.key];
  const isChild = node.parent_key !== null;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-md border border-line p-3',
        isChild && 'bg-cream',
      )}
    >
      <span className="grid min-w-0 grow gap-0.5">
        <span className="flex items-center gap-1.5 text-body font-medium text-ink">
          {isChild && (
            <CornerDownLeft
              size={14}
              strokeWidth={2}
              aria-hidden="true"
              className="shrink-0 text-muted"
            />
          )}
          {node.label || MODULE_LABELS[node.key]}
        </span>
        {effect && (
          <span className="text-label text-muted">
            {node.effective ? 'מוצג' : 'מוסתר'}: {effect}
          </span>
        )}
      </span>

      {/* מצב + מילה, לא רק צבע */}
      {parentOff ? (
        <StatusBadge tone="warn">כבוי דרך ההורה</StatusBadge>
      ) : (
        <StatusBadge tone={node.effective ? 'good' : 'warn'}>
          {node.effective ? 'דולק' : 'כבוי'}
        </StatusBadge>
      )}

      <Button
        variant={node.effective ? 'secondary' : 'primary'}
        disabled={isSaving || parentOff}
        onClick={() => onToggle(!node.effective)}
      >
        {node.effective ? 'לכבות' : 'להדליק'}
      </Button>
    </div>
  );
}

function BudgetRow({
  moduleKey,
  quota,
  moduleOn,
  isSaving,
  onSave,
}: {
  moduleKey: ModuleKey;
  quota: AiQuota | null;
  moduleOn: boolean;
  isSaving: boolean;
  onSave: (limit: number, active: boolean) => void;
}) {
  const limit = quota?.monthly_limit ?? 0;
  const used = quota?.used ?? 0;
  const active = quota?.is_active ?? false;
  const [draft, setDraft] = useState(String(limit));

  const isDirty = draft !== String(limit);
  const parsed = Number(draft);
  const isValid = Number.isInteger(parsed) && parsed >= 0;

  return (
    <div className="grid gap-2 rounded-md border border-line p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body font-medium text-ink">{MODULE_LABELS[moduleKey]}</span>
        <StatusBadge tone={active ? 'good' : 'warn'}>{active ? 'פעיל' : 'כבוי'}</StatusBadge>
        {active && !moduleOn && (
          <StatusBadge tone="warn">
            <AlertTriangle size={14} strokeWidth={2} aria-hidden="true" />
            המודול עצמו כבוי
          </StatusBadge>
        )}
      </div>

      <div className="grid gap-1">
        <span className="text-label text-muted">
          <span className="tabular">{used}</span> מתוך <span className="tabular">{limit}</span>{' '}
          פעולות החודש
          {limit > 0 && used >= limit && ' · נגמר'}
        </span>
        <Progress
          value={Math.min(used, Math.max(limit, 1))}
          max={Math.max(limit, 1)}
          label={`ניצול התקציב של ${MODULE_LABELS[moduleKey]}`}
        />
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="grid gap-1">
          <span className="text-label font-medium">תקרה חודשית</span>
          <input
            type="number"
            min={0}
            inputMode="numeric"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            className={cn(
              'min-h-11 w-32 rounded-sm border bg-surface px-3 text-body text-ink',
              isValid ? 'border-muted' : 'border-bad',
            )}
          />
        </label>

        <Button
          variant="primary"
          disabled={isSaving || !isValid || (!isDirty && active)}
          onClick={() => onSave(parsed, true)}
        >
          <Check size={18} strokeWidth={1.75} aria-hidden="true" />
          {active ? 'שמירת התקרה' : 'הפעלה עם התקרה הזו'}
        </Button>

        {active && (
          <Button variant="secondary" disabled={isSaving} onClick={() => onSave(limit, false)}>
            לכבות
          </Button>
        )}
      </div>

      <p className="m-0 text-label text-muted">תקרה 0 חוסמת לגמרי, גם כשהמודול דלוק.</p>
    </div>
  );
}
